import { API_BASE_URL } from '../config/api';

import { LoginRequest, RegisterRequest, GoogleAuthRequest, AuthResponse } from '../types/auth';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

export const authApi = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      console.log('Attempting to login with email:', credentials.email);
      console.log('API URL:', `${API_BASE_URL}/auth/login`);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include', // Include cookies/credentials for CORS
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      console.log('Login response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Login response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Login failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Login failed');
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'Cannot connect to server. Please check if the backend is running.');
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async googleLogin(googleAuthRequest: GoogleAuthRequest): Promise<AuthResponse> {
    try {
      console.log('Attempting Google login');
      console.log('API URL:', `${API_BASE_URL}/auth/google`);
      
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(googleAuthRequest),
      });

      console.log('Google login response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Google login response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Google login failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Google login failed');
      }

      return data;
    } catch (error) {
      console.error('Google login error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'Cannot connect to server. Please check if the backend is running.');
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      console.log('Attempting to register with data:', { ...userData, password: '[HIDDEN]', confirmPassword: '[HIDDEN]' });
      console.log('API URL:', `${API_BASE_URL}/auth/register`);
      
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include', // Include cookies/credentials for CORS
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      let data;
      try {
        data = await response.json();
        console.log('Response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Registration failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Registration failed');
      }

      return data;
    } catch (error) {
      console.error('Registration error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'Cannot connect to server. Please check if the backend is running.');
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getCurrentUser(token: string): Promise<AuthResponse> {
    try {
      console.log('Getting current user info with token');
      console.log('API URL:', `${API_BASE_URL}/auth/me`);
      
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include', // Include cookies/credentials for CORS
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('getCurrentUser response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('getCurrentUser response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('getCurrentUser failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Failed to get user info');
      }

      return data;
    } catch (error) {
      console.error('getCurrentUser error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiError(0, 'Cannot connect to server. Please check if the backend is running.');
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      console.log('Refreshing access token');
      console.log('API URL:', `${API_BASE_URL}/auth/refresh`);
      
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      console.log('Refresh token response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Refresh token response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Token refresh failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Token refresh failed');
      }

      return data;
    } catch (error) {
      console.error('Token refresh error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async revokeToken(refreshToken: string): Promise<void> {
    try {
      console.log('Revoking refresh token');
      console.log('API URL:', `${API_BASE_URL}/auth/revoke`);
      
      const response = await fetch(`${API_BASE_URL}/auth/revoke`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      console.log('Revoke token response status:', response.status);

      if (!response.ok) {
        console.warn('Token revocation failed, but continuing with logout');
      }
    } catch (error) {
      console.warn('Token revocation error, but continuing with logout:', error);
    }
  },

  async getAllUsers(page?: number, pageSize?: number): Promise<PagedResult<UserInfo>> {
    try {
      const params = new URLSearchParams();
      if (page !== undefined) params.append('page', page.toString());
      if (pageSize !== undefined) params.append('pageSize', pageSize.toString());
      
      const url = `${API_BASE_URL}/auth/users${params.toString() ? '?' + params.toString() : ''}`;
      console.log('Getting all users (admin only)');
      console.log('API URL:', url);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get all users response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get all users response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get all users failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get users');
      }

      return data;
    } catch (error) {
      console.error('Get all users error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log('Changing password');
      console.log('API URL:', `${API_BASE_URL}/auth/change-password`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });

      console.log('Change password response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Change password response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Change password failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Failed to change password');
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log('Requesting password reset for:', email);
      console.log('API URL:', `${API_BASE_URL}/auth/forgot-password`);
      
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      console.log('Forgot password response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Forgot password response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Forgot password failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Failed to request password reset');
      }

      return data;
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async resetPassword(email: string, token: string, newPassword: string, confirmPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log('Resetting password for:', email);
      console.log('API URL:', `${API_BASE_URL}/auth/reset-password`);
      
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, token, newPassword, confirmPassword }),
      });

      console.log('Reset password response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Reset password response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Reset password failed with status:', response.status, 'Message:', data.message);
        throw new ApiError(response.status, data.message || 'Failed to reset password');
      }

      return data;
    } catch (error) {
      console.error('Reset password error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },
};